import {
  type CityDetail,
  CityDetailSchema,
  type CityListQuery,
  type CityListResponse,
  CityListResponseSchema,
  type CityTransport,
  CityTransportSchema,
  type SetCentralStationInput,
  type CountryOption,
  CountryOptionListSchema,
  type CreateCityRequest,
  type StateOption,
  StateOptionListSchema,
  type UpdateCityRequest,
} from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';

/**
 * City Master + geography lookups API surface. Every response is validated with
 * the shared Zod schema so a server change can't sneak past the UI types.
 */

export const listCities = async (query: CityListQuery): Promise<CityListResponse> => {
  const { data } = await apiClient.get<unknown>('/cities', { params: query });
  return CityListResponseSchema.parse(data);
};

export const getCity = async (id: string): Promise<CityDetail> => {
  const { data } = await apiClient.get<unknown>(`/cities/${id}`);
  return CityDetailSchema.parse(data);
};

export const createCity = async (payload: CreateCityRequest): Promise<CityDetail> => {
  const { data } = await apiClient.post<unknown>('/cities', payload);
  return CityDetailSchema.parse(data);
};

export const updateCity = async (id: string, payload: UpdateCityRequest): Promise<CityDetail> => {
  const { data } = await apiClient.patch<unknown>(`/cities/${id}`, payload);
  return CityDetailSchema.parse(data);
};

export const deleteCity = async (id: string): Promise<void> => {
  await apiClient.delete(`/cities/${id}`);
};

// ----- Geography lookups (read-only) -----

export const listCountries = async (): Promise<CountryOption[]> => {
  const { data } = await apiClient.get<unknown>('/countries/options');
  return CountryOptionListSchema.parse(data);
};

export const listStates = async (countryId: string): Promise<StateOption[]> => {
  const { data } = await apiClient.get<unknown>('/states/options', { params: { countryId } });
  return StateOptionListSchema.parse(data);
};

export const bulkDeleteCities = async (
  ids: string[],
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/cities/bulk-delete', { ids });
  return data;
};

export const bulkSetCitiesStatus = async (
  ids: string[],
  isActive: boolean,
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/cities/bulk-set-status', { ids, isActive });
  return data;
};

// ----- Transport anchors (central railway station + airports) -----

export const getCityTransport = async (id: string): Promise<CityTransport> => {
  const { data } = await apiClient.get<unknown>(`/cities/${id}/transport`);
  return CityTransportSchema.parse(data);
};

export const setCityCentralStation = async (
  id: string,
  payload: SetCentralStationInput,
): Promise<CityTransport> => {
  const { data } = await apiClient.put<unknown>(`/cities/${id}/transport/central-station`, payload);
  return CityTransportSchema.parse(data);
};

export const resyncCityTransport = async (id: string): Promise<CityTransport> => {
  // OSM can take a while to answer for a big metro.
  const { data } = await apiClient.post<unknown>(`/cities/${id}/transport/resync`, undefined, {
    timeout: 120_000,
  });
  return CityTransportSchema.parse(data);
};
